import React from 'react';
import { Play } from '@phosphor-icons/react';
import { Banner, BrowserStreamPanel, Button, Text, VideoPlayerWithTimeline } from '@chatsdet/ui';
import type { VideoTimeline, VideoTimelineEntry } from '@chatsdet/types';
import {
  PlanningView,
  type ExploratoryTestEnvironmentInstance,
} from '../../../features/exploratory-tests';
import type { useBrowserStream } from '../../../hooks/useBrowserStream';
import type { ExploratoryBrowserSession } from '../../../features/exploratory-tests/types';
import { API_URL } from '../../../config';

type ExploratoryDetailMediaProps = {
  isPlanning: boolean;
  isRunning: boolean;
  planningStartedAt: string | null;
  environmentInstance: ExploratoryTestEnvironmentInstance | null;
  browserState: ReturnType<typeof useBrowserStream>['state'];
  videoSrc: string | null;
  timeline: VideoTimeline | null;
  onAnnotationClick: (entry: VideoTimelineEntry) => void;
  /** Drop the player's card chrome so it sits flush in a surrounding lockup (e.g. a tab body). */
  flush?: boolean;
  browserSessions: ExploratoryBrowserSession[];
  runId?: string;
  selectedBrowserSessionId?: string | null;
  onBrowserSessionSelect?: (id: string) => void;
};

export function ExploratoryDetailMedia({
  isPlanning,
  isRunning,
  planningStartedAt,
  environmentInstance,
  browserState,
  videoSrc,
  timeline,
  onAnnotationClick,
  flush,
  browserSessions,
  runId,
  selectedBrowserSessionId,
  onBrowserSessionSelect,
}: ExploratoryDetailMediaProps) {
  const selectedSession =
    browserSessions.find((session) => session.id === selectedBrowserSessionId) ??
    browserSessions.find(
      (session) => session.status === 'active' || session.status === 'allocating'
    ) ??
    browserSessions[0];
  const selectedVideoSrc = selectedSession
    ? selectedSession.videoStorageKey && runId
      ? `${API_URL}/adhoc-tests/${runId}/sessions/${selectedSession.id}/video`
      : null
    : videoSrc;
  const selectedTimeline = selectedSession?.videoTimelineJson ?? timeline;
  // A closed session with an error is a missing-evidence warning, not a failure (failed sessions keep their error text).
  const missingEvidence =
    selectedSession?.status === 'closed' && selectedSession.error ? selectedSession.error : null;
  const evidenceWarning = missingEvidence ? (
    <Banner variant="warning" className="mb-3">
      <Text className="text-sm text-inherit">Evidence missing for this session: {missingEvidence}</Text>
    </Banner>
  ) : null;
  if (isPlanning) {
    return (
      <div className="h-full">
        <PlanningView
          planningStartedAt={planningStartedAt}
          environmentInstance={environmentInstance}
          activity={browserState.plannerActivity}
        />
      </div>
    );
  }

  if (
    isRunning &&
    (!selectedSession ||
      selectedSession.status === 'active' ||
      selectedSession.status === 'allocating')
  ) {
    return (
      <div className="h-full">
        <BrowserSessionEvidence
          sessions={browserSessions}
          selectedSession={selectedSession}
          runId={runId}
          onSelect={onBrowserSessionSelect}
        />
        <BrowserStreamPanel
          state={browserState}
          layout="stacked"
          screenshotAspectRatio="aspect-video"
          maxActions={10}
          showReasoningPanel={true}
          showActionHistory={false}
          emptyScreenshotLabel="Waiting for screenshot..."
          emptyReasoningLabel="Waiting for agent..."
        />
      </div>
    );
  }

  if (selectedVideoSrc) {
    // Locked to the video's own 16:9 aspect (the player's default sizing,
    // not `fill`) and top-anchored in a scrollable column — `fill` stretched
    // the player to the full height of whatever column it sat in, letterboxing
    // the actual video inside a tall, mostly-empty box. `shrink-0` keeps the
    // player at its natural aspect-driven height even if the column is taller;
    // `overflow-y-auto` on the wrapper is the fallback if it's ever shorter.
    return (
      <div className="h-full overflow-y-auto">
        {evidenceWarning}
        <BrowserSessionEvidence
          sessions={browserSessions}
          selectedSession={selectedSession}
          runId={runId}
          onSelect={onBrowserSessionSelect}
        />
        <div className="shrink-0">
          <VideoPlayerWithTimeline
            posterSrc={null}
            videoSrc={selectedVideoSrc}
            timeline={selectedTimeline}
            alt="Exploratory test recording"
            onAnnotationClick={onAnnotationClick}
            showChapters
            flush={flush}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      {evidenceWarning}
      <div className="flex min-h-48 items-center justify-center">
        <div className="text-center">
          <Play size={48} className="mx-auto text-muted-foreground/40 mb-2" />
          <Text className="text-muted-foreground">
            {(missingEvidence ? null : selectedSession?.error) ?? 'No recording available'}
          </Text>
        </div>
      </div>
      <BrowserSessionEvidence
        sessions={browserSessions}
        selectedSession={selectedSession}
        runId={runId}
        onSelect={onBrowserSessionSelect}
      />
    </div>
  );
}

function BrowserSessionEvidence({
  sessions,
  selectedSession,
  runId,
  onSelect,
}: {
  sessions: ExploratoryBrowserSession[];
  selectedSession: ExploratoryBrowserSession | undefined;
  runId: string | undefined;
  onSelect: ((id: string) => void) | undefined;
}) {
  if (sessions.length === 0) return null;
  // A lone session without a sandbox copy is the run itself; the run page already shows its trace.
  if (sessions.length === 1 && sessions[0]?.sandboxV2SandboxId === null) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" aria-label="Browser sessions">
      {sessions.map((session, index) => (
        <Button
          key={session.id}
          variant={selectedSession?.id === session.id ? 'secondary' : 'ghost'}
          size="sm"
          onPress={() => onSelect?.(session.id)}
          aria-pressed={selectedSession?.id === session.id}
          tooltip={`${session.status}${session.error ? ` · ${session.error}` : ''}`}
        >
          {session.persona ?? `Session ${index + 1}`}
          {session.sandboxV2SandboxId !== null &&
            ` · copy ${session.sandboxV2SandboxId.slice(0, 6)}`}{' '}
          · {session.status}
        </Button>
      ))}
      {selectedSession?.traceStorageKey && runId && (
        <a
          className="ml-auto text-sm text-primary hover:underline"
          target="_blank"
          rel="noopener noreferrer"
          href={`${API_URL}/adhoc-tests/${runId}/sessions/${selectedSession.id}/trace`}
        >
          Open trace
        </a>
      )}
    </div>
  );
}
